import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Mail, Loader2, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";

export function TestEmailDialog() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("trial_expiry");
  const [lang, setLang] = useState((i18n.language || "de").slice(0, 2));
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [html, setHtml] = useState("");

  const preview = async () => {
    setHtml("");
    try {
      const r = await api.get(`/admin/test-email/preview?kind=${kind}&lang=${lang}`);
      setHtml(r.data.html || "");
    } catch { toast.error(t("adm.teErr")); }
  };
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/test-email`, { kind, lang, to: to || null });
      toast.success(t("adm.teSent", { to: r.data.to }));
    } catch (e) { toast.error(e.response?.data?.detail || t("adm.teErr")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setHtml(""); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-full" data-testid="test-email-btn"><Mail className="mr-1 h-3.5 w-3.5" /> {t("adm.coTestEmail")}</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="test-email-dialog">
        <DialogHeader><DialogTitle>{t("adm.teTitle")}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">{t("adm.teDesc")}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>{t("adm.teKind")}</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="te-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="trial_expiry">{t("adm.teKindExpiry")}</SelectItem>
                <SelectItem value="purge_warning">{t("adm.teKindWarn")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t("adm.teLang")}</Label>
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger data-testid="te-lang"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="de">Deutsch</SelectItem>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="bs">Bosanski</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-2">
          <Label>{t("adm.teTo")}</Label>
          <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder={t("adm.teToPlaceholder")} data-testid="te-to" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-full" onClick={preview} data-testid="te-preview-btn"><Eye className="mr-1 h-4 w-4" /> {t("adm.tePreview")}</Button>
          <Button className="rounded-full bg-navy text-white hover:bg-navy/90" onClick={send} disabled={busy} data-testid="te-send-btn">
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Mail className="mr-1 h-4 w-4" />} {t("adm.teSend")}
          </Button>
        </div>
        {html && (
          <iframe title="email-preview" srcDoc={html} className="mt-2 h-[420px] w-full rounded-xl border border-slate-200" data-testid="te-preview-frame" />
        )}
      </DialogContent>
    </Dialog>
  );
}
